const SOUND_KEY = 'sipdash_admin_sound';

export function isSoundEnabled(): boolean {
  try {
    const stored = localStorage.getItem(SOUND_KEY);
    // Default ON if never set
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, enabled ? 'true' : 'false');
  } catch {
    // ignore
  }
}

function createAudioContext(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    return new Ctx();
  } catch {
    return null;
  }
}

/**
 * Pleasant ascending chime — played when a new order arrives.
 * Uses a C5-E5-G5 arpeggio (clean, attention-grabbing but not harsh).
 */
export function playNewOrderSound(): void {
  if (!isSoundEnabled()) return;
  const ctx = createAudioContext();
  if (!ctx) return;

  const notes = [
    { freq: 523.25, time: 0 },       // C5
    { freq: 659.25, time: 0.13 },    // E5
    { freq: 783.99, time: 0.26 },    // G5
    { freq: 1046.5, time: 0.40 },    // C6 (octave up — strong finish)
  ];

  notes.forEach(({ freq, time }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const startAt = ctx.currentTime + time;

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startAt);

    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(0.35, startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, startAt + 0.45);

    osc.start(startAt);
    osc.stop(startAt + 0.5);
  });

  // Close context after sounds finish
  setTimeout(() => {
    try { ctx.close(); } catch { /* ignore */ }
  }, 1500);
}

/**
 * Descending two-note tone — played when an order is cancelled.
 * Subtle but distinct from the new-order sound.
 */
export function playCancelledOrderSound(): void {
  if (!isSoundEnabled()) return;
  const ctx = createAudioContext();
  if (!ctx) return;

  const notes = [
    { freq: 440, time: 0 },     // A4
    { freq: 329.63, time: 0.2 }, // E4
  ];

  notes.forEach(({ freq, time }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const startAt = ctx.currentTime + time;

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startAt);

    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(0.28, startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, startAt + 0.38);

    osc.start(startAt);
    osc.stop(startAt + 0.4);
  });

  setTimeout(() => {
    try { ctx.close(); } catch { /* ignore */ }
  }, 1200);
}
